import type { CommunityShape, CommunityTextBox, CommunityZonesV2, ZoneBox } from '../../types/api';
import {
  LOGO_MINIMUM,
  SHAPE_KIND_LABELS,
  SHAPE_MINIMUM,
  TEXT_MINIMUM,
  TEXT_ROLES,
  VALUE_MINIMUM,
  clampBox,
  clampPortrait,
  maxDiameterAt,
  portraitBox,
  resizeBox,
  type ResizeCorner,
  type ZoneMinimum,
} from './communityZones';

/**
 * The design builder's layers as one addressable set: a key per layer, the box
 * it occupies, and the clamped arithmetic of moving and resizing it. Pure — the
 * builder's canvas, its layers list and its inspector all speak these keys.
 */

/** A layer on the canvas, by what it is. The backdrop is selectable too. */
export type LayerKey =
  'backdrop' | 'logo' | 'portrait' | 'value' | `text:${string}` | `shape:${string}`;

export function textKey(id: string): LayerKey {
  return `text:${id}`;
}

export function shapeKey(id: string): LayerKey {
  return `shape:${id}`;
}

export function findText(zones: CommunityZonesV2, key: LayerKey): CommunityTextBox | null {
  if (!key.startsWith('text:')) return null;
  const id = key.slice(5);
  return zones.texts.find((text) => text.id === id) ?? null;
}

export function findShape(zones: CommunityZonesV2, key: LayerKey): CommunityShape | null {
  if (!key.startsWith('shape:')) return null;
  const id = key.slice(6);
  return zones.shapes.find((shape) => shape.id === id) ?? null;
}

/** The box a layer occupies, or null when it is not on the canvas. */
export function layerBox(zones: CommunityZonesV2, key: LayerKey): ZoneBox | null {
  switch (key) {
    case 'backdrop':
      return null;
    case 'logo':
      return zones.logo.enabled ? zones.logo.box : null;
    case 'portrait':
      return zones.portrait ? portraitBox(zones.portrait) : null;
    case 'value':
      return zones.value;
    default:
      return findText(zones, key)?.box ?? findShape(zones, key)?.box ?? null;
  }
}

export function minimumOf(key: LayerKey): ZoneMinimum {
  if (key === 'logo') return LOGO_MINIMUM;
  if (key === 'value') return VALUE_MINIMUM;
  if (key.startsWith('shape:')) return SHAPE_MINIMUM;
  return TEXT_MINIMUM;
}

/** The map with one layer's box replaced. A portrait box is read back as a circle. */
export function withLayerBox(
  zones: CommunityZonesV2,
  key: LayerKey,
  box: ZoneBox,
): CommunityZonesV2 {
  switch (key) {
    case 'backdrop':
      return zones;
    case 'logo':
      return { ...zones, logo: { ...zones.logo, box } };
    case 'portrait':
      return {
        ...zones,
        portrait: clampPortrait({ cx: box.x + box.w / 2, cy: box.y + box.h / 2, d: box.w }),
      };
    case 'value':
      return { ...zones, value: box };
    default: {
      const text = findText(zones, key);
      if (text) {
        return {
          ...zones,
          texts: zones.texts.map((entry) => (entry.id === text.id ? { ...entry, box } : entry)),
        };
      }
      const shape = findShape(zones, key);
      if (shape) {
        return {
          ...zones,
          shapes: zones.shapes.map((entry) => (entry.id === shape.id ? { ...entry, box } : entry)),
        };
      }
      return zones;
    }
  }
}

/** A layer's box moved so its corner lands at (x, y), clamped to the canvas. */
export function movedBox(key: LayerKey, start: ZoneBox, x: number, y: number): ZoneBox {
  if (key === 'portrait') {
    const d = start.w;
    return portraitBox(clampPortrait({ cx: x + d / 2, cy: y + d / 2, d }));
  }
  return clampBox({ ...start, x, y }, minimumOf(key));
}

/** A layer's box resized from a corner toward a point. The circle grows from its centre. */
export function resizedBox(
  key: LayerKey,
  start: ZoneBox,
  corner: ResizeCorner,
  point: { fx: number; fy: number },
): ZoneBox {
  if (key === 'portrait') {
    const cx = start.x + start.w / 2;
    const cy = start.y + start.h / 2;
    const wanted = 2 * Math.max(Math.abs(point.fx - cx), Math.abs(point.fy - cy));
    const d = Math.min(wanted, maxDiameterAt(cx, cy));
    return portraitBox(clampPortrait({ cx, cy, d }));
  }
  return resizeBox(start, corner, point, minimumOf(key));
}

/** What a layer is called in the list, on the canvas and to a screen reader. */
export function layerName(zones: CommunityZonesV2, key: LayerKey): string {
  switch (key) {
    case 'backdrop':
      return 'Backdrop';
    case 'logo':
      return 'Logo';
    case 'portrait':
      return 'Portrait circle';
    case 'value':
      return 'Value box';
    default: {
      const text = findText(zones, key);
      if (text) return text.label.trim() || TEXT_ROLES[text.role].label;
      const shape = findShape(zones, key);
      // `s3` reads as "Rectangle 3"; an id of any other shape is shown as written.
      if (shape) return `${SHAPE_KIND_LABELS[shape.kind]} ${shape.id.replace(/^s(?=\d+$)/, '')}`;
      return 'Layer';
    }
  }
}
